import { useState, useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import { authState } from "../../State/Auth";
import { pushNotificationsState } from "../../State/PushNotifications";
import { registerServiceWorker } from "../../PushNotifications/registerServiceWorker";
import {
  fetchVapidPublicKey,
  subscribeToPush,
  sendSubscriptionToBackend,
  removeSubscriptionFromBackend,
} from "../../PushNotifications/pushSubscription";
interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  shouldShowOptIn: boolean;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
  dismissOptIn: () => void;
}

function getIsSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function getDismissedKey(userId: string): string {
  return `push_prompt_dismissed:${userId}`;
}

function getActiveKey(userId: string): string {
  return `push_subscription_active:${userId}`;
}

// Module-level deduplication: ensures the re-subscription check runs only once
// per userId+token combination, even when multiple components call the hook.
let resubscribePromise: Promise<boolean> | null = null;
let resubscribeKey = "";

async function runResubscribeCheck(
  userId: string,
  token: string,
): Promise<boolean> {
  const key = `${userId}:${token}`;
  if (resubscribePromise && resubscribeKey === key) {
    return resubscribePromise;
  }

  resubscribeKey = key;
  resubscribePromise = (async () => {
    const currentPermission = window.Notification.permission;
    const wasActive = localStorage.getItem(getActiveKey(userId)) === "true";

    if (currentPermission !== "granted") {
      if (wasActive) {
        localStorage.setItem(getActiveKey(userId), "false");
      }
      return false;
    }

    // Permission is granted — check if browser still has a push subscription.
    // This handles logout/login (localStorage.clear()) and cleared site data:
    // the browser keeps the PushManager subscription even when localStorage is wiped.
    const registration = await registerServiceWorker();
    if (!registration) return false;

    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub && wasActive) {
      // Everything is in sync — subscription exists and localStorage agrees. No backend call needed.
      return true;
    } else if (existingSub && !wasActive) {
      // Browser subscription exists but localStorage was cleared (logout/cleared site data).
      // Re-register with backend to restore state.
      const sent = await sendSubscriptionToBackend(existingSub, token);
      if (sent) {
        localStorage.setItem(getActiveKey(userId), "true");
        return true;
      }
    } else if (wasActive) {
      // localStorage said active but browser subscription is gone — re-subscribe
      const vapidKey = await fetchVapidPublicKey(token);
      if (!vapidKey) return false;

      const subscription = await subscribeToPush(registration, vapidKey);
      if (!subscription) return false;

      const sent = await sendSubscriptionToBackend(subscription, token);
      if (sent) {
        localStorage.setItem(getActiveKey(userId), "true");
        return true;
      }
    }

    return false;
  })();

  return resubscribePromise;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [auth] = useRecoilState(authState);
  const [pushState, setPushState] = useRecoilState(pushNotificationsState);
  const [permission, setPermission] = useState<NotificationPermission>(
    getIsSupported() ? window.Notification.permission : "default"
  );
  const [dismissed, setDismissed] = useState(true);

  const isSupported = getIsSupported();
  const userId = auth.clientUser?.id ?? "";
  const token = auth.token ?? "";

  // Load dismissed state from localStorage
  useEffect(() => {
    if (!userId) return;
    const isDismissed = localStorage.getItem(getDismissedKey(userId)) === "true";
    setDismissed(isDismissed);
  }, [userId]);

  // Re-subscription check on mount — deduplicated across all hook instances
  useEffect(() => {
    if (!isSupported || !userId || !token) return;

    runResubscribeCheck(userId, token).then((subscribed) => {
      setPermission(window.Notification.permission);
      if (subscribed) {
        setPushState({ isSubscribed: true });
      } else if (window.Notification.permission !== "granted") {
        setPushState({ isSubscribed: false });
      }
    });
  }, [isSupported, userId, token]);

  const shouldShowOptIn =
    isSupported &&
    permission === "default" &&
    !dismissed &&
    !pushState.isSubscribed;

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !token) return false;

    // Explicitly request desktop notification permission before subscribing
    const permissionResult = await window.Notification.requestPermission();
    setPermission(permissionResult);

    if (permissionResult !== "granted") {
      if (userId) {
        localStorage.setItem(getDismissedKey(userId), "true");
        setDismissed(true);
      }
      return false;
    }

    const registration = await registerServiceWorker();
    if (!registration) return false;

    const vapidKey = await fetchVapidPublicKey(token);
    if (!vapidKey) return false;

    const subscription = await subscribeToPush(registration, vapidKey);

    if (!subscription) {
      return false;
    }

    // Send subscription to backend
    const sent = await sendSubscriptionToBackend(subscription, token);
    if (!sent) {
      // Retry once
      const retried = await sendSubscriptionToBackend(subscription, token);
      if (!retried) {
        console.error("Failed to register push subscription with backend");
        return false;
      }
    }

    if (userId) {
      localStorage.setItem(getActiveKey(userId), "true");
    }
    setPushState({ isSubscribed: true });
    return true;
  }, [isSupported, token, userId, setPushState]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!isSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        if (token) {
          await removeSubscriptionFromBackend(subscription.endpoint, token);
        }
      }
    } catch (error) {
      console.error("Error during unsubscription:", error);
    }

    if (userId) {
      localStorage.setItem(getActiveKey(userId), "false");
    }
    setPushState({ isSubscribed: false });
  }, [isSupported, token, userId, setPushState]);

  const dismissOptIn = useCallback((): void => {
    if (userId) {
      localStorage.setItem(getDismissedKey(userId), "true");
    }
    setDismissed(true);
  }, [userId]);

  // Public API for re-enabling from settings
  // This is handled by the subscribe/unsubscribe methods above.
  // When re-enabling:
  //   - permission === 'granted': subscribe() will silently re-subscribe
  //   - permission === 'default': subscribe() will trigger browser prompt
  //   - permission === 'denied': caller should show toast instead

  return {
    isSupported,
    permission,
    isSubscribed: pushState.isSubscribed,
    shouldShowOptIn,
    subscribe,
    unsubscribe,
    dismissOptIn,
  };
};
